<template>
    <button
        :class="{ 'zs-button': true, round: round, [`size-${size}`]: size, [`weight-${weight}`]: weight, [color]: color, [`text-align-${textAlign}`]: textAlign }"
        v-bind="$attrs" :disabled="disabled" @click="onClick">
        <div class="holder">
            <ZSIcon v-if="icon" :rotation="iconRotation" :color="iconColor" :size="iconSize" :icon="icon" :flipH="iconFlipH" :flipV="iconFlipV" :spin="iconSpin" :spinDirection="iconSpinDirection" />
            <slot></slot>
        </div>
    </button>
</template>

<script setup>
// import ZSIcon from '@/components/zs-components/global/ZSIcon.vue'

const props = defineProps({
    round: {
        type: Boolean,
        default: false
    },
    size: {
        type: String,
        default: '',
        validator: (value) => ['', 'small', 'extra-small', 'large', 'extra-large', 'small-text', 'badge', 'block'].includes(value)
    },
    iconSize: {
        type: String,
        default: 'medium'
    },
    iconRotation: {
        type: [Number, String],
        default: 0
    },
    iconFlipH: {
        type: Boolean,
        default: false
    },
    iconFlipV: {
        type: Boolean,
        default: false
    },
    iconSpin: {
        type: Boolean,
        default: false
    },
    iconSpinDirection: {
        type: String,
        default: 'normal'
    },
    weight: {
        type: String,
        default: 'medium',
        validator: (value) => ['light', 'medium', 'normal', 'bold', 'black'].includes(value)
    },
    disabled: {
        type: Boolean,
        default: false
    },
    icon: {
        type: String,
        default: ''
    },
    iconColor: {
        type: String,
        default: 'default'
    },
    color: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'primary', 'secondary', 'success', 'danger', 'orange', 'warning', 'info', 'white', 'green', 'light-green', 'gray', 'red'].includes(value)
    },
    textAlign: {
        type: String,
        default: 'center',
        validator: (value) => ['center', 'left', 'right'].includes(value)
    }
})

const emit = defineEmits(['click'])

const onClick = (event) => {
    emit('click', event)
}
</script>

<style>
:root {
    /* Inferred from the 'danger'/'orange' button style in the image */
    --color-button-danger: #D35400;
    --color-button-danger-hover: #C04600;
    /* A plausible reddish-orange/burnt orange */
    --color-text-danger: #FFFFFF;
    /* White text/icon for danger buttons */
    /* TODO: Define hover/text variants if needed, e.g., --color-button-danger-hover */

    /* Plausible defaults based on common UI practices and image */
    --zs-button-height: 38px;
    --zs-button-padding: 0 16px;
    --zs-button-gap: 8px;
    --zs-button-border-radius: 8px;
    --zs-button-font-size: 1rem;
    --zs-button-font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    --zs-button-transition: 0.2s ease;

    /* Font Weights */
    --zs-button-weight-light: 300;
    --zs-button-weight-normal: 400;
    --zs-button-weight-medium: 500;
    /* Default */
    --zs-button-weight-bold: 700;
    --zs-button-weight-black: 900;

    /* Default Button Style Variables */
    --zs-button-border-color: #CED4DA;
    /* Default border color */
    --zs-button-color: #212529;
    /* Default text color */
    --color-white: #FFFFFF;
    --color-button-default: #F8F9FA;
    --color-button-default-hover: #E9ECEF;
    --color-text-default: #212529;
    --zs-button-hover-bg: var(--color-button-default-hover);
    /* Default hover background */
    --zs-button-hover-color: var(--color-text-default);
    /* Default hover text color */


    /* Define other color variables used in the component (using placeholders) */
    --color-button-primary: #0d6efd;
    /* Example Blue */
    --color-text-primary: #FFFFFF;
    --color-button-primary-hover: #0b5ed7;

    --color-button-secondary: #6c757d;
    /* Example Gray */
    --color-text-secondary: #FFFFFF;
    --color-button-secondary-hover: #5c636a;

    --color-button-success: #198754;
    /* Example Green */
    --color-text-success: #FFFFFF;
    --color-button-success-hover: #157347;

    --color-button-light-success: #d1e7dd;
    /* Example Light Green */
    --color-text-light-success: #0a3622;
    --color-button-light-success-hover: #bcd0c7;
    --color-text-light-green: var(--color-text-light-success);
    /* Alias often used */


    --color-button-warning: #ffc107;
    /* Example Yellow */
    --color-text-warning: #000000;
    --color-button-warning-hover: #ffca2c;

    --color-medium-grey: #adb5bd;
    /* For Info */
    --color-dark-grey: #495057;
    /* For Info text */
    --color-gray-dark: #343a40;
    /* For Info Hover */

    --color-blue: #0d6efd;
    /* For White button text/icon */


    --color-button-disabled: #E9ECEF;
    --color-text-disabled: #6C757D;


    /* Define size variables (using placeholders) */
    --zs-button-small-height: 32px;
    --zs-button-small-font-size: 12px;
    --zs-button-extra-small-height: 28px;
    --zs-button-extra-small-font-size: 11px;
    --zs-button-badge-size: 24px;
    --zs-button-badge-font-size: 10px;

    /* Define size variables for large and extra-large */
    --zs-button-large-height: 42px;
    --zs-button-large-font-size: 18px;
    --zs-button-extra-large-height: 48px;
    --zs-button-extra-large-font-size: 20px;

}

button.zs-button {
    /* Base styles */
    height: var(--zs-button-height);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--zs-button-padding);
    gap: var(--zs-button-gap);
    background: var(--color-white);
    border: 1px solid var(--zs-button-border-color);
    border-radius: var(--zs-button-border-radius);
    color: var(--zs-button-color);
    font-size: var(--zs-button-font-size);
    font-family: var(--zs-button-font-family);
    transition: var(--zs-button-transition);
    cursor: pointer;
    width: fit-content;

    &.block,
    &.size-block {
        width: 100%;
    }

    .holder {
        white-space: nowrap;
    }

    &:hover {
        background: var(--zs-button-hover-bg);
        color: var(--zs-button-hover-color);
    }

    /* Size variations */
    &.size-small {
        height: var(--zs-button-small-height);
        font-size: var(--zs-button-small-font-size);
    }

    &.size-extra-small {
        height: var(--zs-button-extra-small-height);
        font-size: var(--zs-button-extra-small-font-size);
    }

    &.size-large {
        height: var(--zs-button-large-height);
        font-size: var(--zs-button-large-font-size);
    }

    &.size-extra-large {
        height: var(--zs-button-extra-large-height);
        font-size: var(--zs-button-extra-large-font-size);
    }

    &.size-small-text {
        font-size: var(--zs-button-small-font-size);
    }

    &.size-badge {
        padding: 0;
        width: var(--zs-button-badge-size);
        height: var(--zs-button-badge-size);
        font-size: var(--zs-button-badge-font-size);
    }

    /* Width variations */
    &.auto-width {
        width: auto;
        min-width: 160px;
    }

    /* Weight variations */
    &.weight-light {
        font-weight: var(--zs-button-weight-light);
    }

    &.weight-normal {
        font-weight: var(--zs-button-weight-normal);
    }

    &.weight-medium {
        font-weight: var(--zs-button-weight-medium);
    }

    &.weight-bold {
        font-weight: var(--zs-button-weight-bold);
    }

    &.weight-black {
        font-weight: var(--zs-button-weight-black);
    }

    /* Color variations */
    &.blue,
    &.primary {
        background-color: var(--color-button-primary);
        border-color: var(--color-button-primary);
        color: var(--color-text-primary);

        &:hover {
            background-color: var(--color-button-primary-hover);
            border-color: var(--color-button-primary-hover);
            color: var(--color-text-primary);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-button-primary);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-text-primary);
        }
    }

    &.secondary {
        background-color: var(--color-button-secondary);
        border-color: var(--color-button-secondary);
        color: var(--color-text-secondary);

        &:hover {
            background-color: var(--color-button-secondary-hover);
            border-color: var(--color-button-secondary-hover);
            color: var(--color-text-secondary);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-button-secondary);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-text-secondary);
        }
    }

    &.orange,
    &.danger,
    &.red {
        background-color: var(--color-button-danger);
        border-color: var(--color-button-danger);
        color: var(--color-text-danger);

        &:hover {
            background-color: var(--color-button-danger-hover);
            border-color: var(--color-button-danger-hover);
            color: var(--color-text-danger);
        }

        .zs-icon {
            fill: var(--color-text-danger);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-button-danger);

            &:hover {
                text-decoration: underline;
            }

            .zs-icon {
                fill: var(--color-button-danger);
            }
        }
    }

    &.green,
    &.success {
        background-color: var(--color-button-success);
        border-color: var(--color-button-success);
        color: var(--color-text-success);

        &:hover {
            background-color: var(--color-button-success-hover);
            color: var(--color-text-success);
            border-color: var(--color-button-success-hover);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-button-success);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-text-success);
        }
    }

    &.light-green {
        background-color: var(--color-button-light-success);
        border-color: var(--color-button-light-success);
        color: var(--color-text-light-success);

        &:hover {
            background-color: var(--color-button-light-success-hover);
            border-color: var(--color-button-light-success-hover);
            color: var(--color-text-light-success);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-button-light-success);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-text-light-green);
        }
    }

    &.yellow,
    &.warning {
        background-color: var(--color-button-warning);
        border-color: var(--color-button-warning);
        color: var(--color-text-warning);

        &:hover {
            background-color: var(--color-button-warning-hover);
            border-color: var(--color-button-warning-hover);
            color: var(--color-text-warning);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-button-warning);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-text-warning);
        }
    }

    &.gray,

    &.default {
        background-color: var(--color-button-default);
        border-color: var(--color-button-default);
        color: var(--color-text-default);

        &:hover {
            background-color: var(--color-button-default-hover);
            border-color: var(--color-button-default-hover);
            color: var(--color-text-default);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-text-default);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-text-default);
        }
    }

    &.info {
        background-color: var(--color-medium-grey);
        border-color: var(--color-medium-grey);
        color: var(--color-dark-grey);

        &:hover {
            background-color: var(--color-dark-grey);
            border-color: var(--color-gray-dark);
            color: var(--color-white);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-dark-grey);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-white);
        }
    }

    &.white {
        background-color: #FFFFFF;
        border-color: transparent;
        color: var(--color-blue);

        &:hover {
            background-color: #F8F8F8;
            /* b“order-color: #CCCCCC; */
            color: var(--color-blue);
        }

        &.text {
            background-color: transparent;
            border-color: transparent;
            color: var(--color-blue);

            &:hover {
                text-decoration: underline;
            }
        }

        .zs-icon {
            fill: var(--color-blue);
        }
    }

    /* Disabled state */
    &:disabled {
        background-color: var(--color-button-disabled) !important;
        border-color: var(--color-button-disabled) !important;
        color: var(--color-text-disabled) !important;
        cursor: not-allowed;
        opacity: 0.7;

        &:hover {
            background-color: var(--color-button-disabled) !important;
            border-color: var(--color-button-disabled) !important;
            color: var(--color-text-disabled) !important;
        }

        .zs-icon {
            fill: var(--color-text-disabled) !important;
        }
    }

    .holder {
        display: flex;
        align-items: center;
        width: 100%;
        gap: var(--zs-button-gap);
    }

    &.round {
        border-radius: 50%;
        padding: 0;
        width: var(--zs-button-height);
    }

    &.size-small.round {
        width: var(--zs-button-small-height);
    }

    &.size-extra-small.round {
        width: var(--zs-button-extra-small-height);
    }

    &.size-large.round {
        width: var(--zs-button-large-height);
    }

    &.size-extra-large.round {
        width: var(--zs-button-extra-large-height);
    }

    &.text-align-left {
        .holder {
            justify-content: flex-start;
        }
    }

    &.text-align-center {
        .holder {
            justify-content: center;
        }
    }

    &.text-align-right {
        .holder {
            justify-content: flex-end;
        }
    }
}
</style>
